import { useState } from "react";
import { Link } from "react-router-dom";
import type { Title } from "@/mocks/titles";

const detailTabs = ["剧情简介", "主创信息", "关联标签"] as const;
type DetailTab = (typeof detailTabs)[number];

export default function DetailHero({ item }: { item: Title }) {
  const [tab, setTab] = useState<DetailTab>("剧情简介");
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl = `${window.location.origin}/detail/${item.id}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <section className="relative w-full overflow-hidden bg-primary-950">
      <div className="absolute inset-0">
        <img
          src={item.backdrop}
          alt={`${item.title} 剧照背景`}
          className="h-full w-full object-cover object-top opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/90 to-primary-950/40"></div>
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-primary-950 to-transparent"></div>
      </div>

      <div className="relative z-10 mx-auto max-w-[1440px] px-4 py-8 md:px-8 md:py-12">
        <div className="flex flex-col gap-6 md:flex-row md:gap-10">
          <div className="w-[160px] shrink-0 self-center overflow-hidden rounded-lg border border-background-200/20 bg-primary-900 md:w-[240px] md:self-start">
            <img
              src={item.cover}
              alt={`${item.title} 封面海报`}
              title={`${item.title} 海报`}
              className="aspect-[3/4] h-full w-full object-cover object-top"
            />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to={`/category/${item.type}`}
                className="rounded-md bg-secondary-500 px-2.5 py-1 text-[11px] font-medium text-primary-950 whitespace-nowrap transition-colors cursor-pointer hover:bg-secondary-400"
              >
                {item.type === "movie" ? "电影" : "电视剧"}
              </Link>
              {item.badge && (
                <span className="rounded-md bg-accent-500 px-2.5 py-1 text-[11px] text-background-50">{item.badge}</span>
              )}
              <span className="text-[11px] text-primary-200">{item.episodes || item.duration}</span>
            </div>

            <h1 className="mt-3 font-heading text-3xl font-bold leading-tight text-background-50 md:text-4xl">
              {item.title}
            </h1>
            <p className="mt-1.5 text-xs tracking-[0.18em] text-primary-200">{item.englishTitle}</p>

            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-background-200">
              <span className="flex items-center gap-1.5 rounded-md bg-background-50/10 px-2.5 py-1 backdrop-blur">
                <i className="ri-star-fill text-secondary-400" aria-hidden="true"></i>
                <strong className="font-medium text-background-50">{item.rating.toFixed(1)}</strong>
                <span className="text-[11px] text-primary-200">/ 10</span>
              </span>
              <span>{item.year}</span>
              <span>{item.region}</span>
              <span className="flex items-center gap-1">
                <i className="ri-fire-line text-accent-400" aria-hidden="true"></i>
                热度 {item.heat}
              </span>
            </div>

            <p className="mt-5 max-w-2xl font-heading text-base leading-relaxed text-background-100 md:text-lg">
              「{item.tagline}」
            </p>

            <dl className="mt-6 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
              <div className="flex gap-3">
                <dt className="w-14 shrink-0 text-primary-300">导演</dt>
                <dd className="text-background-100">{item.director}</dd>
              </div>
              <div className="flex gap-3">
                <dt className="w-14 shrink-0 text-primary-300">主演</dt>
                <dd className="text-background-100">{item.cast.join(" / ")}</dd>
              </div>
              <div className="flex gap-3">
                <dt className="w-14 shrink-0 text-primary-300">类型</dt>
                <dd className="flex flex-wrap gap-1.5">
                  {item.genres.map((genre) => (
                    <Link
                      key={genre}
                      to={`/category/${item.type}/list`}
                      className="rounded-md border border-background-200/25 px-1.5 py-0.5 text-[11px] text-background-200 transition-colors cursor-pointer hover:border-secondary-400 hover:text-secondary-400"
                    >
                      {genre}
                    </Link>
                  ))}
                </dd>
              </div>
              <div className="flex gap-3">
                <dt className="w-14 shrink-0 text-primary-300">制片地区</dt>
                <dd className="text-background-100">{item.region}</dd>
              </div>
            </dl>

            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to={`/category/${item.type}`}
                className="inline-flex items-center gap-2 rounded-md bg-secondary-500 px-5 py-2.5 text-sm font-medium text-primary-950 whitespace-nowrap transition-colors cursor-pointer hover:bg-secondary-400"
              >
                <i className="ri-compass-3-line" aria-hidden="true"></i>
                浏览{item.type === "movie" ? "更多电影" : "更多剧集"}
              </Link>
              <button
                type="button"
                onClick={() => setShareOpen(true)}
                className="inline-flex items-center gap-2 rounded-md border border-background-200/35 px-5 py-2.5 text-sm text-background-100 whitespace-nowrap transition-colors cursor-pointer hover:bg-background-50/10"
              >
                <i className="ri-share-forward-line" aria-hidden="true"></i>
                分享给朋友
              </button>
            </div>
          </div>
        </div>

        <div className="mt-10 rounded-lg border border-background-200/15 bg-primary-950/50 p-4 backdrop-blur md:p-6">
          <div className="flex flex-wrap items-center gap-1 rounded-full bg-background-50/10 p-1 sm:w-fit">
            {detailTabs.map((item2) => (
              <button
                key={item2}
                type="button"
                onClick={() => setTab(item2)}
                className={`rounded-full px-4 py-1.5 text-xs whitespace-nowrap transition-colors cursor-pointer ${
                  tab === item2 ? "bg-background-50 text-primary-950 font-medium" : "text-background-200 hover:text-background-50"
                }`}
              >
                {item2}
              </button>
            ))}
          </div>

          <div className="mt-4">
            {tab === "剧情简介" && (
              <p className="max-w-4xl text-sm leading-7 text-background-200">{item.synopsis}</p>
            )}
            {tab === "主创信息" && (
              <div className="grid grid-cols-1 gap-4 text-sm text-background-200 sm:grid-cols-2">
                <div>
                  <p className="text-xs text-primary-300">导演</p>
                  <p className="mt-1 text-background-100">{item.director}</p>
                  <p className="mt-3 text-xs text-primary-300">单片时长</p>
                  <p className="mt-1 text-background-100">{item.duration}</p>
                </div>
                <div>
                  <p className="text-xs text-primary-300">主要演员</p>
                  <ul className="mt-1 space-y-1">
                    {item.cast.map((actor) => (
                      <li key={actor} className="text-background-100">
                        {actor}
                      </li>
                    ))}
                  </ul>
                </div>
                <p className="text-[11px] leading-relaxed text-primary-300/80 sm:col-span-2">
                  以上演职人员信息为演示内容，与真实人物无关。
                </p>
              </div>
            )}
            {tab === "关联标签" && (
              <div className="flex flex-wrap gap-2">
                {[...item.genres, item.region, String(item.year), item.type === "movie" ? "电影" : "电视剧"].map(
                  (tag) => (
                    <span
                      key={tag}
                      className="rounded-full border border-background-200/25 px-3 py-1 text-xs text-background-200"
                    >
                      #{tag}
                    </span>
                  ),
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {shareOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-end justify-center bg-primary-950/70 p-4 animate-fade-in md:items-center"
          role="dialog"
          aria-modal="true"
          onClick={() => setShareOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-lg bg-background-50 p-5 md:p-6"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="font-heading text-lg font-bold text-foreground-950">分享这部作品</h2>
                <p className="mt-1 text-xs text-foreground-500">复制链接，把《{item.title}》推荐给朋友</p>
              </div>
              <button
                type="button"
                onClick={() => setShareOpen(false)}
                aria-label="关闭分享弹窗"
                className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-500 cursor-pointer hover:bg-background-100"
              >
                <i className="ri-close-line" aria-hidden="true"></i>
              </button>
            </div>

            <div className="mt-4 flex items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 py-2.5">
              <i className="ri-link text-foreground-400" aria-hidden="true"></i>
              <span className="min-w-0 flex-1 truncate text-xs text-foreground-700">{shareUrl}</span>
              <button
                type="button"
                onClick={copyLink}
                className="rounded-md bg-primary-800 px-3 py-1.5 text-[11px] text-background-50 whitespace-nowrap cursor-pointer hover:bg-primary-700"
              >
                {copied ? "已复制" : "复制"}
              </button>
            </div>

            <div className="mt-4 grid grid-cols-3 gap-2">
              {[
                { icon: "ri-wechat-line", label: "微信" },
                { icon: "ri-weibo-line", label: "微博" },
                { icon: "ri-message-2-line", label: "留言" },
              ].map((channel) => (
                <span
                  key={channel.label}
                  className="flex flex-col items-center gap-1.5 rounded-md bg-background-100 py-3 text-[11px] text-foreground-600"
                >
                  <i className={`${channel.icon} text-base text-primary-700`} aria-hidden="true"></i>
                  {channel.label}
                </span>
              ))}
            </div>

            <p className="mt-4 rounded-md bg-secondary-100 px-3 py-2 text-[11px] text-secondary-900">
              演示功能：分享仅生成站内链接，不会向第三方平台发送内容。
            </p>
          </div>
        </div>
      )}
    </section>
  );
}